import {Form, Modal, Spin} from 'antd';
import styles from './walletDetails.module.scss';
import {useNavigate, useParams} from 'react-router-dom';
import {GET_BOTH_PLATFORM_AND_WALLET_CURRENCY} from 'graphql/queries/wallet';
import {useMutation, useQuery} from '@apollo/client';
import {useEffect} from 'react';
import DepositWalletFields from './WalletFields/DepositWalletField';
import WithdrawalWalletFields from './WalletFields/WithdrawalWalletFields';
import backIcon from 'images/back.png';
import {checkFeeValues} from '../create/CreateProviderSettings';
import {handleUpdatePaymentProviderSetting} from '../actions/MutationRequests';
import {toast} from 'react-toastify';
import {UPDATE_PAYMENT_PROVIDER_SETTINGS} from 'graphql/mutations/wallet';

const CurrencyDetails = ({currency: currencyProp, embedded}) => {
  const {provider, currency: currencyParam} = useParams();
  const currency = currencyProp ?? currencyParam;
  const [form] = Form.useForm();

  const navigate = useNavigate();

  const [updatePaymentProviderCurrencySettings, {loading: loadingUpdate}] =
    useMutation(UPDATE_PAYMENT_PROVIDER_SETTINGS);

  const handleUpdate = values => {
    if (
      checkFeeValues(
        values?.withdrawalFees ?? [],
        Number(values.minWithdrawalAmount),
      )
    )
      return toast.error(
        'Fee Values must be LESS than Minimum Withdrawal Amount',
      );

    if (
      checkFeeValues(values?.depositFees ?? [], Number(values.minDepositAmount))
    )
      return toast.error('Fee Values must be LESS than Minimum Deposit Amount');

    Modal.confirm({
      title: `Update ${currency} settings for ${provider}?`,
      content: `This changes the live deposit and withdrawal rules every user sees for ${currency} on ${provider}.`,
      okText: 'Yes, Update',
      cancelText: 'Cancel',
      onOk: () =>
        handleUpdatePaymentProviderSetting(
          updatePaymentProviderCurrencySettings,
          values,
          provider,
          true,
          currency,
          values?.depositFees,
          values?.withdrawalFees,
          values?.depositDiscount,
          values?.withdrawalDiscount,
          () => navigate('/dashboard/wallet'),
        ),
    });
  };

  // Without this the button silently does nothing when a required field
  // (often one scrolled out of view) fails validation.
  const handleUpdateFailed = ({errorFields}) => {
    toast.error(
      errorFields?.[0]?.errors?.[0] ??
        'Please fill in all the required fields before updating',
    );
  };

  const {loading, data} = useQuery(GET_BOTH_PLATFORM_AND_WALLET_CURRENCY, {
    variables: {
      providerName: provider,
      currency,
      currency2: currency,
    },
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  });

  useEffect(() => {
    const currencies = data?.getPaymentProviderByProviderName?.currencies;
    if (!currencies) return;

    const {feeSchedule, discount} = currencies?.[currency] ?? {};

    /**
     * Stored schedules contain placeholder rows with no type and no amount —
     * 12 of the 34 provider/currency pairs have at least one. Rendering those
     * as form rows made every one of their fields fail "This is a Required
     * Field", so those pairs could not be saved at all. Drop them on load.
     */
    const isRealFee = fee =>
      !!fee?.feeType ||
      fee?.fixedFeeAmount != null ||
      fee?.variableFeeAmount != null;

    const collect = (entries, depositKey, withdrawalKey) =>
      (entries ?? []).reduce(
        (acc, {operationType, fee, paymentMethodType}) => {
          const formatted = (fee ?? []).filter(isRealFee).map(f => ({
            variableFee: f.variableFeeAmount,
            fixedFee: f.fixedFeeAmount,
            feeType: f.feeType,
            paymentMethodType,
          }));
          if (!formatted.length) return acc;
          if (operationType === 'DEPOSIT') acc[depositKey].push(...formatted);
          if (operationType === 'WITHDRAWAL')
            acc[withdrawalKey].push(...formatted);
          return acc;
        },
        {[depositKey]: [], [withdrawalKey]: []},
      );

    const fees = collect(feeSchedule, 'depositFees', 'withdrawalFees');
    // Previously an early `return` here when a record had no discounts, which
    // meant its fee rows never reached the form either.
    const discounts = collect(
      discount,
      'depositDiscount',
      'withdrawalDiscount',
    );

    form.setFieldsValue({
      ...fees,
      ...discounts,
    });
  }, [data?.getPaymentProviderByProviderName?.currencies, currency]);

  if (loading) {
    return (
      <div className="query-loading">
        <Spin size="large" />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div className={embedded ? undefined : styles.root}>
      {!embedded && (
        <div className={styles.header}>
          <img
            onClick={() =>
              navigate(`/dashboard/wallet/view-details/${provider}`)
            }
            src={backIcon}
          />
          <h1>{currency} WALLET DETAILS</h1>
        </div>
      )}
      <Form
        onFinish={handleUpdate}
        onFinishFailed={handleUpdateFailed}
        scrollToFirstError
        form={form}
        layout="vertical">
        {currency && (
          <>
            <DepositWalletFields
              selectedCurrency={currency}
              form={form}
              currencyLimits={
                data?.getSupportedCurrencyByCurrencyName?.deposit?.limit
              }
              currencyDetails={
                data?.getPaymentProviderByProviderName?.currencies
              }
            />
            <WithdrawalWalletFields
              selectedCurrency={currency}
              currencyLimits={
                data?.getSupportedCurrencyByCurrencyName?.withdrawal?.limit
              }
              loading={loadingUpdate}
              form={form}
              currencyDetails={
                data?.getPaymentProviderByProviderName?.currencies
              }
            />
          </>
        )}
      </Form>
    </div>
  );
};

export default CurrencyDetails;
